import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import { DownloadIcon, rightDirectionBlackIcon } from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import { componentKey, setCloseDataImportData } from "../SettingsSlice";
import { getDataImportList, patchDataImport } from "../SettingsSaga";
import UploadImport from "../dialogs/UploadImport";
import { postBulkDataImportApiResponse } from "../../../utils/constantsFunctions";
import ViewDialogForDataImport from "../dialogs/add-role-dailog/ViewDailogForDataImport";
import moment from "moment";
import ImportedDataSummeryDialog from "../dialogs/ImportedDataSummeryDialog";

const DataImport = () => {
  const { closeDataImportDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const { dataImportList } = useSelector((state: any) => state[componentKey]);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [status, setStatus] = useState("");

  const deviceType = useDeviceType();
  //OPEN THE DIALOG FOR BULK IMPORT
  const [openImport, setOpenImport] = useState(false);
  //STATE TO HOLD THE FILE
  const [file, setFile] = useState<File | null>(null);

  //VIEW DIALOG FOR DATA IMPORT
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [defaultDataForDataImport, setDefaultDataForDataImport] = useState<any>(
    {}
  );
  const [importDataSummery, setImportDataSummery] = useState({
    isOpen: false,
    data: {},
  });
  //COLUMN DATA
  const DataImportColumns = [
    {
      field: "Import Initiated",
      label: <div>Import Initiated</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.importInitiated || "-"}
          </p>
        );
      },
    },
    {
      field: "Entity",
      label: <div>Entity</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.entity || "-"}</p>
        );
      },
    },
    {
      field: "File name",
      label: <div>File Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.fileName || "-"}</p>
        );
      },
    },
    {
      field: "User Name",
      label: <div>User Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.username || "-"}</p>
        );
      },
    },
    {
      field: "Total Records",
      label: <div>Total Records</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.totalRecord || "-"}
          </p>
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any, index: any) => {
        const status =
          row?.status === "Active"
            ? true
            : row?.status === "InActive"
              ? false
              : false;
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <EventEHRSwitchButton
              checked={status}
              onChange={() => {
                const apiPayload = {
                  payload: {
                    status: status ? "InActive" : "Active",
                  },
                  id: row?.id,
                };
                dispatch(patchDataImport(apiPayload));
              }}
            />
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "View Details",
                    onClickCb: () => {
                      setDefaultDataForDataImport(row);
                      setOpenViewDialog(true);
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  //DATA IMPORT API CALLED
  useEffect(() => {
    getDataImport();
  }, [searchedValue, rowPerPage, pageNumber, status]);

  useEffect(() => {
    if (closeDataImportDialog) {
      dispatch(setCloseDataImportData(false));
      setOpenImport(false);
      getDataImport();
    }
  }, [closeDataImportDialog]);

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  //POST DATA IMPORT AS FILE
  const addDataImport = async (saveData: any) => {
    if (!saveData?.file) {
      return;
    }

    // Ensure new FormData object is created
    const data = new FormData();
    data.append("entity", saveData?.entity);
    if (saveData?.file) {
      data.append("file", saveData?.file);
    } else {
      alert("Please select a valid file to upload.");
      return;
    }

    try {
      const response = await postBulkDataImportApiResponse(data, dispatch);
      if (response.success) {
        setImportDataSummery({ isOpen: true, data: response });
        dispatch(setCloseDataImportData(true));
        // Reset file after successful upload
        setFile(null);
        setOpenImport(false);
      }
    } catch (error) {
      dispatch(setCloseDataImportData(false));
      console.error("Error during data import:", error);
    }
  };

  //PASSING PAYLOAD TO GET API FOR DATA IMPORT
  const getDataImport = () => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      name: searchedValue?.trim(),
      status: status ? status : "",
    };
    dispatch(getDataImportList(payload));
  };

  const importDataList = dataImportList?.data?.map((item: any) => ({
    id: item?.id,
    entity: item?.entity,
    username: item?.username,
    totalRecord: item?.totalRecord,
    fileName: item?.fileName,
    status: item?.status,
    importInitiated: item?.createdAt
      ? moment(item?.createdAt).format("MM/DD/YYYY")
      : "-",
  }));

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title={`Data Import`} customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}></div>
            <div>
              <SelectDropdown
                label=""
                name="status"
                placeholderValue="All Status"
                displayEmpty={true}
                value={status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "In Active", value: "InActive" },
                ]}
                onChangeCb={(e) => {
                  setPageNumber(1);
                  setStatus(e.target.value);
                }}
              />
            </div>

            <Button
              startIcon={
                <img
                  src={DownloadIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Import CSV"
              variant="outlined"
              onClickCb={() => {
                setOpenImport(true);
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
                color: "#727272",
                border: "1px solid #D2D2D2",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={DataImportColumns}
            rows={importDataList}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: dataImportList?.totalPages,
              totalCount: dataImportList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {openImport && (
        <UploadImport
          isOpen={openImport}
          handleClose={() => {
            // Clear previous file
            setFile(null);
            setOpenImport(false);
          }}
          handleData={async (data) => {
            await addDataImport(data);
          }}
        />
      )}
      <ViewDialogForDataImport
        dialogHeading="View Data-Import Details"
        handleClose={() => setOpenViewDialog(false)}
        defaultDataForDataImport={defaultDataForDataImport}
        isOpen={openViewDialog}
      />
      <ImportedDataSummeryDialog
        isOpen={importDataSummery?.isOpen}
        handleClose={() => {
          setImportDataSummery({ isOpen: false, data: {} });
        }}
        summeryData={importDataSummery?.data}
        dialogHeading="Imported Data"
      />
    </WrapperCard>
  );
};

export default DataImport;
